import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import { useAppStore } from '../../state/useAppStore'
import { ZoomIn, ZoomOut, Home, Target } from 'lucide-react'

const Map2D = () => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  
  const { 
    countiesData, 
    loading, 
    error, 
    selectedCounty,
    setSelectedCounty,
    setDrilldownUAT,
    mapZoom,
    setMapZoom,
    mapOffset,
    setMapOffset,
    loadCountiesData 
  } = useAppStore()

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [hoveredCounty, setHoveredCounty] = useState(null)
  const [projection, setProjection] = useState(null)
  const [pathGenerator, setPathGenerator] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [renderError, setRenderError] = useState(null)

  // Load data on mount
  useEffect(() => {
    if (!countiesData) {
      loadCountiesData()
    }
  }, [])

  // Setup dimensions and projection
  useEffect(() => {
    if (!containerRef.current) return

    const updateDimensions = () => {
      const { width, height } = containerRef.current.getBoundingClientRect()
      setDimensions({ width: Math.max(width, 100), height: Math.max(height, 100) })
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)

    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  // Initialize projection and path generator
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) {
      return
    }

    try {
      // Centered on Romania
      const center = [25.0, 46.0]
      const scale = Math.max(
        Math.min(dimensions.width, dimensions.height) * 0.8,
        100 // Minimum scale
      )
      
      const proj = d3.geoMercator()
        .center(center)
        .scale(scale)
        .translate([
          dimensions.width / 2 + (mapOffset?.x || 0), 
          dimensions.height / 2 + (mapOffset?.y || 0)
        ])

      const path = d3.geoPath().projection(proj)
      
      setProjection(proj)
      setPathGenerator(path)
      setRenderError(null)
    } catch (err) {
      console.error('Error creating projection:', err)
      setRenderError('Failed to initialize map projection')
    }
  }, [dimensions, mapOffset])

  // Render map
  useEffect(() => {
    if (!svgRef.current || !countiesData || !pathGenerator || !projection) {
      return
    }

    try {
      const svg = d3.select(svgRef.current)
      svg.selectAll('*').remove()

      // Draw counties
      svg.selectAll('path')
        .data(countiesData.features)
        .enter()
        .append('path')
        .attr('d', pathGenerator)
        .attr('class', 'county-path')
        .attr('data-county', d => d.properties?.name || 'Unknown')
        .style('fill', d => {
          const isSelected = selectedCounty === d.properties?.name
          const isHovered = hoveredCounty === d.properties?.name
          return isSelected ? 'url(#selected-gradient)' : 
                 isHovered ? 'url(#hover-gradient)' : 
                 'url(#default-gradient)'
        })
        .style('stroke', d => {
          const isSelected = selectedCounty === d.properties?.name
          return isSelected ? '#00f7ff' : 'rgba(255, 255, 255, 0.3)'
        })
        .style('stroke-width', d => selectedCounty === d.properties?.name ? 2 : 1)
        .style('cursor', 'pointer')
        .style('opacity', 0.9)
        .style('transition', 'all 0.3s ease')
        .on('mouseover', function(event, d) {
          const countyName = d.properties?.name || 'Unknown'
          setHoveredCounty(countyName)
          d3.select(this)
            .style('filter', 'url(#glow)')
            .style('stroke-width', 2)

          // Update tooltip
          if (tooltipRef.current) {
            tooltipRef.current.style.opacity = 1
            tooltipRef.current.style.left = `${event.pageX + 10}px`
            tooltipRef.current.style.top = `${event.pageY - 10}px`
            tooltipRef.current.innerHTML = `
              <div class="font-orbitron font-bold">${countyName}</div>
              <div class="text-xs">Code: ${d.properties?.cod || 'N/A'}</div>
              <div class="text-xs">Click to drill down</div>
            `
          }
        })
        .on('mouseout', function() {
          setHoveredCounty(null)
          d3.select(this)
            .style('filter', 'none')
            .style('stroke-width', selectedCounty === d3.select(this).attr('data-county') ? 2 : 1)

          // Hide tooltip
          if (tooltipRef.current) {
            tooltipRef.current.style.opacity = 0
          }
        })
        .on('click', (event, d) => {
          const countyName = d.properties?.name
          if (countyName) {
            setSelectedCounty(countyName)
            setDrilldownUAT(true)
          }
        })

      // Add gradient definitions
      const defs = svg.append('defs')

      // Default gradient
      defs.append('linearGradient')
        .attr('id', 'default-gradient')
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '100%')
        .selectAll('stop')
        .data([
          { offset: '0%', color: 'rgba(20, 20, 40, 0.7)' },
          { offset: '100%', color: 'rgba(30, 30, 60, 0.9)' }
        ])
        .enter()
        .append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color)

      // Hover gradient
      defs.append('linearGradient')
        .attr('id', 'hover-gradient')
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '100%')
        .selectAll('stop')
        .data([
          { offset: '0%', color: 'rgba(0, 247, 255, 0.3)' },
          { offset: '100%', color: 'rgba(157, 0, 255, 0.2)' }
        ])
        .enter()
        .append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color)

      // Selected gradient
      defs.append('linearGradient')
        .attr('id', 'selected-gradient')
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '100%')
        .selectAll('stop')
        .data([
          { offset: '0%', color: 'rgba(0, 247, 255, 0.5)' },
          { offset: '100%', color: 'rgba(157, 0, 255, 0.4)' }
        ])
        .enter()
        .append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color)

      // Glow filter for hover effect
      const filter = defs.append('filter')
        .attr('id', 'glow')
        .attr('x', '-50%')
        .attr('y', '-50%')
        .attr('width', '200%')
        .attr('height', '200%')

      filter.append('feGaussianBlur')
        .attr('stdDeviation', '3')
        .attr('result', 'coloredBlur')

      const feMerge = filter.append('feMerge')
      feMerge.append('feMergeNode').attr('in', 'coloredBlur')
      feMerge.append('feMergeNode').attr('in', 'SourceGraphic')

    } catch (err) {
      console.error('Error rendering map:', err)
      setRenderError('Failed to render map')
    }
  }, [countiesData, pathGenerator, projection, selectedCounty, hoveredCounty])

  // Mouse handlers for panning
  const handleMouseDown = (event) => {
    setIsDragging(true)
    setDragStart({ 
      x: event.clientX - (mapOffset?.x || 0), 
      y: event.clientY - (mapOffset?.y || 0) 
    })
  }

  const handleMouseMove = (event) => {
    if (!isDragging) return
    setMapOffset({
      x: event.clientX - dragStart.x,
      y: event.clientY - dragStart.y
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleZoom = (direction) => {
    const newZoom = direction === 'in' ? mapZoom * 1.2 : mapZoom / 1.2
    setMapZoom(Math.min(Math.max(newZoom, 0.5), 3))
  }

  const handleReset = () => {
    setMapZoom(1)
    setMapOffset({ x: 0, y: 0 })
    setSelectedCounty(null)
  }

  // Handle wheel zoom
  useEffect(() => {
    const handleWheel = (event) => {
      event.preventDefault()
      const direction = event.deltaY > 0 ? 'out' : 'in'
      handleZoom(direction)
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false })
      return () => container.removeEventListener('wheel', handleWheel)
    }
  }, [mapZoom])

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-monster-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-monster-cyan font-orbitron">Loading Quantum Map...</p>
          <p className="text-sm text-gray-400">Fetching geographic data streams</p>
        </div>
      </div>
    )
  }

  if (error || renderError) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center p-8 monster-card max-w-md">
          <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <div className="w-6 h-6 text-red-400">⚠️</div>
          </div>
          <h3 className="text-xl font-orbitron mb-2">Map Error</h3>
          <p className="text-gray-400 mb-4">{error || renderError}</p>
          <button 
            onClick={loadCountiesData}
            className="monster-btn"
          >
            Retry Connection
          </button>
        </div>
      </div>
    )
  }

  if (!countiesData || !projection) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-monster-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-monster-cyan font-orbitron">Initializing Map...</p>
          <p className="text-sm text-gray-400">Setting up projection system</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-50 pointer-events-none opacity-0 transition-opacity duration-200"
        style={{
          background: 'rgba(10, 10, 26, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 247, 255, 0.3)',
          borderRadius: '12px',
          padding: '12px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
          maxWidth: '200px'
        }}
      />

      {/* Map Container */}
      <div 
        ref={containerRef}
        className="relative w-full h-full overflow-hidden rounded-2xl monster-card"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        {/* SVG Map */}
        <svg
          ref={svgRef}
          width={Math.max(dimensions.width, 100)}
          height={Math.max(dimensions.height, 100)}
          style={{
            transform: `scale(${mapZoom})`,
            transformOrigin: 'center'
          }}
        />

        {/* Map Controls */}
        <div className="absolute bottom-6 right-6 flex flex-col gap-3">
          <button
            onClick={() => handleZoom('in')}
            className="p-3 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border hover:bg-monster-card transition-all duration-300"
          >
            <ZoomIn className="w-5 h-5 text-monster-cyan" />
          </button>
          <button
            onClick={() => handleZoom('out')}
            className="p-3 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border hover:bg-monster-card transition-all duration-300"
          >
            <ZoomOut className="w-5 h-5 text-monster-cyan" />
          </button>
          <button
            onClick={handleReset}
            className="p-3 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border hover:bg-monster-card transition-all duration-300"
          >
            <Home className="w-5 h-5 text-monster-cyan" />
          </button>
          <button
            onClick={() => setSelectedCounty(null)}
            className="p-3 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border hover:bg-monster-card transition-all duration-300"
          >
            <Target className="w-5 h-5 text-monster-cyan" />
          </button>
        </div>

        {/* Zoom Display */}
        <div className="absolute top-6 left-6">
          <div className="px-4 py-2 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border">
            <div className="text-sm font-orbitron">ZOOM: {(mapZoom * 100).toFixed(0)}%</div>
            <div className="text-xs text-gray-400">Drag to pan • Scroll to zoom</div>
          </div>
        </div>

        {/* Data Info */}
        <div className="absolute top-6 right-6">
          <div className="px-4 py-3 rounded-xl bg-monster-card/80 backdrop-blur-xs border border-monster-border">
            <div className="text-sm font-orbitron mb-2">MAP DATA</div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-monster-cyan" />
                <span className="text-xs">
                  {countiesData?.features?.length || 0} Counties
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs">Live Stream</span>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Overlay */}
        {(loading || !projection) && (
          <div className="absolute inset-0 bg-monster-dark/80 backdrop-blur-sm flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-monster-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-monster-cyan font-orbitron">Rendering Quantum Map</p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default Map2D